import React, { useState, useEffect, memo, useMemo, useCallback } from 'react'
import {
  Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableRow, Typography,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ClassNameMap, withStyles } from '@mui/styles'
import { useSelector } from 'react-redux'
import CardLayoutComponent from '../common/card-layout.component'
import NotesComponent from '../common/notes.component'
import ManualOverrideComponent from '../common/manual-override.component'
import ThresholdCardComponent from './threshold-card.component'
import { RootState } from '../../../../redux/store'
import stylesheet from '../detail-container.stylesheet'
import Helper from '../../../../utils/helper'
import ColorBubbleComponent from '../common/color-bubble.component'
import { formatDisplayName, hasDifferentPrefix, formatOptionLabel } from '../../../../utils/display-name-formatter'

interface IProps {
  input: any
  classes: ClassNameMap
}

const LazyAccordion: React.FC<{
  label: React.ReactNode, children: () => React.ReactNode
}> = ({ label, children }) => {
  const [hasOpened, setHasOpened] = useState(false)
  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      sx={{ boxShadow: 'none', '&:before': { display: 'none' }, backgroundColor: 'transparent' }}
      onChange={(_, expanded) => { if (expanded) setHasOpened(true) }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.8rem !important' }} />}
        sx={{ minHeight: 28, px: 0.5, '& .MuiAccordionSummary-content': { margin: '4px 0' } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.65rem', color: '#757575' }}>
          {label}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {hasOpened && children()}
      </AccordionDetails>
    </Accordion>
  )
}

const DiagnosticThresholdComponent: React.FC<IProps> = ({ input, classes }) => {
  const [prgList, setPrgList] = useState<string[]>([])
  const [selectedNotes, setSelectedNotes] = useState<number[]>([])
  const [viewThreshold, setViewThreshold] = useState<any>(null)
  const [itemModes, setItemModes] = useState<Record<string, string>>({})
  const [diagnosticPredicates, setDiagnosticPredicates] = useState([]) as any
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])


  useEffect(() => {
    const prgList: string[] = []
    if (input?.isActionItems && input?.actionProgram) {
      prgList.push(input.actionProgram)
    } else {
      patientDetail?.diagnosis?.diagnostic_predicates.forEach((e: any) => {
        if (Array.isArray(e.programs)) {
          prgList.push(...e.programs)
        } else if (typeof e.programs === 'string') {
          prgList.push(e.programs)
        }
      })
    }

    const uniquePrgList = Array.from(new Set(prgList))
    setPrgList(uniquePrgList)
    setDiagnosticPredicates(patientDetail?.diagnosis?.diagnostic_predicates || [])
  }, [patientDetail?.diagnosis?.diagnostic_predicates])

  const toggleNotes = useCallback((id: any) => {
    setSelectedNotes(prev =>
      prev.includes(+id) ? prev.filter(noteId => noteId !== +id) : [...prev, +id]
    )
  }, [])

  const renderRightHeader = useCallback(
    (value: any) => (
      <Typography variant="body2" className={classes.cardValue}>
        <strong>{value ? Helper.toCamelCaseObject(formatOptionLabel(value)) : '-'}</strong>
      </Typography>
    ),
    [classes.cardValue]
  )

  const handleSetEdit = useCallback((id: string) => {
    const threshold = diagnosticPredicates.find((item: any) => item.id === id)
    setViewThreshold(threshold || null)
  }, [diagnosticPredicates])

  const icons = useMemo(() => {
    if (input.isActionItems) return []

    return [
      { iconType: 'description', callback: toggleNotes },
      { iconType: 'more', callback: {} }
    ]
  }, [input.isActionItems, toggleNotes])

  const updateItemMode = useCallback((id: any, mode: string) => {
    setItemModes(prev => ({ ...prev, [id]: mode }))
  }, [])

  const handleOverrideSubmit = useCallback((id: any) => {
    updateItemMode(id, 'view')
  }, [updateItemMode])

  const renderSubHeader = useCallback((item: any) => {
    const thresholdText = item.threshold ? `(${item.threshold})-` : ''
    const valueText = item.current_value ? `${thresholdText}${item.current_value}` : thresholdText
    return valueText ? <>&nbsp;{valueText}</> : ''
  }, [])

  const getRowClass = useCallback((index: number, dataLength: number) => {
    const isLastRow = index + 1 === dataLength
    return isLastRow ? classes.programTargetLastTableCell : classes.programTargetTableCell
  }, [classes.programTargetLastTableCell, classes.programTargetTableCell])

  const programIncludesItem = useCallback((programs: any, targetProgram: string) => {
    if (!programs) return false
    return Array.isArray(programs) ? programs.includes(targetProgram) : programs === targetProgram
  }, [])

  const getPredicateByPrg = useCallback((prg: string) => {
    if (!prg) return []

    const filteredData = input.isActionItems
      ? diagnosticPredicates?.filter((item: any) =>
        item.needs_update && programIncludesItem(item.programs, input.actionProgram)
      )
      : diagnosticPredicates?.filter((item: any) => programIncludesItem(item.programs, prg))

    return filteredData || []
  }, [diagnosticPredicates, input.isActionItems, input.actionProgram, programIncludesItem])

  const renderDiagnosticCards = useCallback((prg: string) => {
    let filteredData = getPredicateByPrg(prg)

    if (input.isActionItems) {
      filteredData = filteredData?.filter((item: any) => !item.is_overridden && item.needs_update)
    }

    if (!filteredData || filteredData.length === 0) return null

    // Sort by ORIGINAL display_name (with prefix) to preserve grouping
    const sortedData = [...filteredData].sort((a: any, b: any) =>
      (a?.display_name || '').toLowerCase().localeCompare((b?.display_name || '').toLowerCase())
    )

    return sortedData?.map((item: any, index: number) => {
        // Check if we need a separator line (prefix changed from previous item)
        const prevItem = index > 0 ? sortedData[index - 1] : null
        const showSeparator = prevItem && hasDifferentPrefix(prevItem.display_name, item.display_name)
        const itemMode = itemModes[item.id] || 'view'
        const rowClassName = item.is_overridden && item.needs_update
          ? classes.overrideTargetRow
          : item.needs_update
            ? classes.confirmCardBackground
            : classes.updatedTargetRow

        return (
          <React.Fragment key={`threshold-${item.id}-${index}`}>
            <TableRow className={rowClassName}>
              <TableCell
                className={getRowClass(index, sortedData.length)}
                scope="column"
                sx={showSeparator ? { borderTop: '1px solid var(--color-border-medium) !important' } : undefined}
              >
                {itemMode === 'view' && (
                  <CardLayoutComponent
                    id={item.id}
                    name={item.name}
                    leftHeader={formatDisplayName(item.display_name) || item.name}
                  rightHeader={renderRightHeader(item?.value || '')}
                  subHeader={renderSubHeader(item)}
                  bottomText=""
                  input={{
                    dependent_questionnaires: item.dependent_questionnaires || [],
                    dependent_measurements: item.dependent_measurements || [],
                    tests_per_cond: item?.tests_per_cond || [],
                    conditions: item.cond_per_threshold || [],
                    synopsis: item.synopsis || '',
                    rationale: item.rationale || ''
                  }}
                  needs_confirmation={item.needs_confirmation || false}
                  needs_update={item.needs_update || false}
                  programs={item.programs}
                  is_overridden={item.is_overridden || (item.overridden_programs?.length || 0) > 0}
                  overridenPrograms={item.overridden_programs}
                  onOverrideClick={() => updateItemMode(item.id, 'override')}
                  icons={icons}
                  onRefClick={() => null}
                  is_referred={item.is_referred || false}
                  entityRef="threshold"
                  patientId={patientDetail?.id}
                  category="measurements"
                />
              )}

              {selectedNotes.includes(item.id) && (
                <NotesComponent
                  entityId={item.id}
                  entityRef="threshold"
                  cancelBtn={true}
                  onCancel={toggleNotes}
                />
              )}

              {itemMode === 'override' && (
                <ManualOverrideComponent
                  entityRef="threshold"
                  input={item}
                  leftHeader={formatDisplayName(item.display_name) || item.name}
                  rightHeader={renderRightHeader(item.value)}
                  subHeader={item.unit || item.default_unit || ''}
                  programs={item.programs}
                  closeOverride={() => updateItemMode(item.id, 'view')}
                  onOverrideSubmit={handleOverrideSubmit}
                />
              )}
            </TableCell>
            </TableRow>
          </React.Fragment>
        )
      })
  }, [
    getPredicateByPrg,
    input.isActionItems,
    itemModes,
    classes.overrideTargetRow,
    classes.confirmCardBackground,
    classes.updatedTargetRow,
    getRowClass,
    renderRightHeader,
    renderSubHeader,
    icons,
    patientDetail?.id,
    selectedNotes,
    toggleNotes,
    updateItemMode,
    handleOverrideSubmit
  ])

  const shouldShowCard = useMemo(() => {
    if (!input.isActionItems) return true
    return diagnosticPredicates.some((item: any) => item.needs_update && !item.is_overridden)
  }, [input.isActionItems, diagnosticPredicates])

  console.timeEnd('DiagnosticThreshold render')
  return (
    <Box className={classes.customCard}>
      {shouldShowCard && (
        <Grid item className={classes.fullWidth}>
          {prgList.map((p: string, i: number) => {
            if (!selectedProgram.includes(p)) return null

            const programEnrollment = program_enrollments.find((e: any) => e.program === p)
            const cardClassName = classes.compactTargetCard
            return ((input?.isActionItems && getPredicateByPrg(p)?.some((e: any) => e.needs_update && e.is_overridden !== true))
              || !input?.isActionItems)
              ? <Box key={`props-${i}`} className={classes.programTargetBox}>
                {(() => {
                  const predicateCount = (getPredicateByPrg(p) || []).length
                  const needsUpdateCount = (getPredicateByPrg(p) || []).filter((item: any) => item.needs_update && !item.is_overridden).length
                  const isLargeList = predicateCount > 15

                  const renderTable = (showHeader: boolean) => (
                    <TableContainer>
                      <Table className={classes.table} aria-label="diagnostic thresholds table">
                        <TableBody>
                          {showHeader && (
                            <TableRow>
                              <TableCell
                                colSpan={2}
                                className={`${classes.programTargetFirstTableCell} ${classes.programTargetFirstTableInputTitle}`}
                                scope="row"
                              >
                                <Typography variant="subtitle2" pb={0.1} className={classes.programTargetHeader}>
                                  {programEnrollment?.program_display_name}
                                  <ColorBubbleComponent selectedProgramList={[p]} />
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {renderDiagnosticCards(p)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )

                  if (isLargeList) {
                    return (
                      <LazyAccordion
                        label={<>
                          Diagnostic Thresholds ({predicateCount})
                          {needsUpdateCount > 0 && (
                            <span style={{ color: '#ed6c02', marginLeft: 8 }}>
                              {needsUpdateCount} needs update
                            </span>
                          )}
                          <ColorBubbleComponent selectedProgramList={[p]} />
                        </>}
                      >
                        {() => (
                          <Card variant="outlined" className={cardClassName}>
                            <CardContent className={classes.programTargetContainer}>
                              {renderTable(false)}
                            </CardContent>
                          </Card>
                        )}
                      </LazyAccordion>
                    )
                  }

                  return (
                    <Card variant="outlined" className={cardClassName}>
                      <CardContent className={classes.programTargetContainer}>
                        {renderTable(true)}
                      </CardContent>
                    </Card>
                  )
                })()}
              </Box>
              : ''
          })}
        </Grid>
      )}

      {viewThreshold && (
        <ThresholdCardComponent
          input={viewThreshold}
          open={true}
          handleClose={() => setViewThreshold(null)}
        />
      )}
    </Box>
  )
}

export default withStyles(stylesheet)(memo(DiagnosticThresholdComponent))